import type { CSSProperties } from 'react'

import {
  PALETTE,
  colourOfTrack,
  paletteColourForId,
  type PaletteColour,
} from '@anytracker/core'

/**
 * Colours for tracks, check-ins and categories, from the six named palette
 * colours (see `PALETTE` in core).
 *
 * A track's colour is the one chosen in its editor, or the one its id gives
 * when nobody has chosen. Either way the decision is made in core, so the
 * screen, the printed report and the native app agree on it (T-404). A track
 * that is blue in the app and orange in the report makes the two impossible
 * to discuss side by side.
 *
 * Each colour comes in shades, as CSS custom properties so they follow the
 * theme:
 *
 *   fill  the colour itself. Squares, swatches, heat. Decoration only: the
 *         lighter hues do not reach 3:1 on white.
 *   tint  a pale wash behind a check-in's header or a tag.
 *   edge  the border of a tinted area.
 *   deep  lines, and text on its own tint or on a surface. Meets 3:1 as a
 *         line and 4.5:1 as text, in both themes.
 *
 * Colour never carries meaning on its own: every series is named in a legend
 * and available as a table.
 */
export type Shade = 'fill' | 'tint' | 'edge' | 'deep'

export function paletteVar(colour: PaletteColour, shade: Shade): string {
  return `var(--hue-${colour}-${shade})`
}

interface Colourable {
  readonly id: string
  readonly colour?: PaletteColour
}

/** A track's line colour: chart strokes, and swatches that must stay visible. */
export function trackColour(track: Colourable): string {
  return paletteVar(colourOfTrack(track), 'deep')
}

/** A track's fill, for the small square that marks it beside its name. */
export function trackFill(track: Colourable): string {
  return paletteVar(colourOfTrack(track), 'fill')
}

/** A track's tint, for the unfilled part of a bar drawn in its fill. */
export function trackTint(track: Colourable): string {
  return paletteVar(colourOfTrack(track), 'tint')
}

/**
 * All four shades of one hue as `--hue-*` custom properties, for an element
 * whose descendants are tinted in it: a check-in's header band, say. The
 * stylesheet then reads `var(--hue-tint)` and so on without knowing which hue.
 */
export function hueStyle(colour: PaletteColour): CSSProperties {
  return {
    '--hue-tint': paletteVar(colour, 'tint'),
    '--hue-edge': paletteVar(colour, 'edge'),
    '--hue-deep': paletteVar(colour, 'deep'),
    '--hue-fill': paletteVar(colour, 'fill'),
  } as CSSProperties
}

/** A check-in's colour. Always worked out from its id; check-ins do not store one. */
export function checkInColour(template: { readonly id: string }): PaletteColour {
  return paletteColourForId(template.id)
}

/** Distinct colours for the options of one enum track, in order. */
export function categoryColour(index: number): string {
  return paletteVar(PALETTE[index % PALETTE.length] ?? 'leaf', 'deep')
}

/**
 * Dash patterns, so an overlay survives a greyscale printer.
 *
 * These reports get printed on office printers, and eight distinct hues become
 * eight indistinguishable greys (T-504). Colour is therefore never the only
 * thing separating two lines: the pattern is, and the legend carries it too so
 * the two can still be matched up by eye.
 *
 * Solid first, because a single-series chart should not look dashed for no
 * reason.
 */
const DASHES: readonly string[] = [
  '',
  '6 3',
  '2 3',
  '9 3 2 3',
  '1 3',
  '12 4',
  '6 3 2 3 2 3',
  '4 2 1 2',
]

/** `undefined` for a solid line, which is what SVG wants for "no dashes". */
export function seriesDash(index: number): string | undefined {
  const pattern = DASHES[index % DASHES.length]
  return pattern === '' ? undefined : pattern
}
